import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { trackCtaClick } from '../analytics/ctaGoal';
import { visitorCurrency, planPrice } from '../pricing/prices';
import '../App.css';

/**
 * The fantasy writing software page (#1136), step 2 of the SEO track: a page for the
 * searches "fantasy writing software" and "worldbuilding software", which return no
 * Chaptery page today.
 *
 * Every string is the owner-approved copy in
 * docs/superpowers/specs/2026-11-01-fantasy-page-design.md, checked by claims-check.
 * The comparison is from each tool's own website as of 1 November 2026; recheck the
 * sources listed in the spec before changing it.
 *
 * The screenshots are the demo account's fantasy book, "The Coin and the Void".
 * Not on the ad pixel's allowlist (AdPixelGate): the privacy policy names exactly the
 * pages the pixel loads on.
 */

type Shot = { name: string; w: number; h: number; alt: string };

const SHOTS: Record<'map' | 'world' | 'storyboard' | 'print', Shot> = {
  map: {
    name: 'fantasy-1-relationships', w: 2540, h: 1520,
    alt: "Chaptery's World Building window for the fantasy novel The Coin and the Void. Character cards for Caiga, Vess Orand, Sil Morwen, Arath and Brother Clement are joined by labelled links such as ally, rival, enemy and complicated, and Caiga's details are open in a side panel.",
  },
  world: {
    name: 'fantasy-2-world', w: 2596, h: 1558,
    alt: "A chapter titled Voidfire open in Chaptery's manuscript editor, beside the World Building window showing the book's characters, locations and events and the links between them.",
  },
  storyboard: {
    name: 'fantasy-3-storyboard', w: 2540, h: 1520,
    alt: "Chaptery's Storyboard for The Coin and the Void. Colour-coded plot notes are each linked to a character such as Caiga, Sil Morwen or Arath, and the selected note is open in a side panel with its linked character.",
  },
  print: {
    name: 'fantasy-4-print', w: 2596, h: 1558,
    alt: "Chaptery's print preview in Book as designed mode, showing the opening pages of the chapter Dice and Debt with a drop cap and a crossed-swords scene divider from the Fantasy set.",
  },
};

/* No width or height attributes: CSS sizes these, and the aspect-ratio from each file's
   2x size (w, h) reserves its space while it loads. */
const FantasyShot = ({ shot, hero }: { shot: Shot; hero?: boolean }) => (
  <img
    src={`/img/fantasy/${shot.name}.png`}
    srcSet={`/img/fantasy/${shot.name}.png ${shot.w / 2}w, /img/fantasy/${shot.name}@2x.png ${shot.w}w`}
    sizes={hero
      ? '(max-width: 1140px) calc(100vw - 40px), 1100px'
      : '(max-width: 768px) calc(100vw - 40px), (max-width: 1200px) calc(50vw - 50px), 550px'}
    alt={shot.alt}
    className={`fantasy-shot${hero ? ' fantasy-shot--hero' : ''}`}
    style={{ aspectRatio: `${shot.w} / ${shot.h}` }}
    {...(hero ? { fetchPriority: 'high' as const } : { loading: 'lazy' as const, decoding: 'async' as const })}
  />
);

const FantasyPage: React.FC = () => {
  const [currency] = React.useState(visitorCurrency);
  return (
    <>
      <RouteSeo path="/fantasy-writing-software" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Chaptery', item: 'https://trychaptery.com' },
            { '@type': 'ListItem', position: 2, name: 'Fantasy Writing Software', item: 'https://trychaptery.com/fantasy-writing-software' },
          ],
        })}</script>
      </Helmet>

      <div className="features-page fantasy-page">
        <section className="features-hero">
          <div className="container">
            <div className="features-tag">For fantasy and sci-fi authors</div>
            <h1>Build your world. Write the series.</h1>
            <p className="features-hero-sub">
              Chaptery is fantasy writing software for Mac and Windows. Your manuscript sits
              beside your characters, realms and history, and a single world can hold every
              book in your series.
            </p>
            <div className="hero-actions">
              <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('fantasy')}>
                Start Free Trial
              </Link>
              <Link to="/features" className="btn-secondary-large">See All Features</Link>
            </div>
            <p className="hero-trial-note">30 days free, every feature, one book. No credit card.</p>
            <FantasyShot shot={SHOTS.map} hero />
          </div>
        </section>

        <section className="features-split" aria-labelledby="fantasy-world-heading">
          <div className="container features-split-inner">
            <div className="features-split-text">
              <div className="features-tag">World Builder</div>
              <h2 id="fantasy-world-heading">Your world, beside the manuscript</h2>
              <p>
                Fantasy asks you to remember more than most books. Build your world on
                trychaptery.com and keep it open beside the chapter you're writing.
              </p>
              <ul className="features-list">
                <li><span><strong>Characters:</strong> role, archetype, origin, fatal flaw, motivation, arc and voice notes</span></li>
                <li><span><strong>Locations:</strong> climate, terrain, landmarks, government, customs and beliefs, economy, founding and current state</span></li>
                <li><span><strong>Events:</strong> when they happen, how long they last, what caused them, what followed and whose point of view they're seen from</span></li>
              </ul>
            </div>
            <div className="features-split-visual">
              <FantasyShot shot={SHOTS.world} />
            </div>
          </div>
        </section>

        <section className="features-split features-split--alt" aria-labelledby="fantasy-map-heading">
          <div className="container fantasy-text-section">
            <div className="features-tag">Relationship map</div>
            <h2 id="fantasy-map-heading">See how everything connects</h2>
            <p>
              Link your entries on a map: who rules which city, which realm borders which,
              which battle caused the next.
            </p>
            <ul className="features-list">
              <li>Characters become allies, rivals, mentors or family</li>
              <li>A character rules a place, lives there or fled from it</li>
              <li>Places contain one another and share borders</li>
              <li>Events cause, precede and prevent other events</li>
              <li>Add your own relationship types, roles and archetypes when your world needs words the defaults lack</li>
            </ul>
          </div>
        </section>

        <section className="features-split" aria-labelledby="fantasy-series-heading">
          <div className="container fantasy-text-section">
            <div className="features-tag">Series</div>
            <h2 id="fantasy-series-heading">One world across the whole series</h2>
            <p>
              Group your books into a series on your Bookshelf and attach characters and places
              to the whole series or to the books they appear in. Deep in book three? Filter the
              map to book three and you'll see its own entries alongside the ones you've
              attached to the whole series.
            </p>
          </div>
        </section>

        <section className="features-split features-split--alt" aria-labelledby="fantasy-storyboard-heading">
          <div className="container features-split-inner">
            <div className="features-split-visual">
              <FantasyShot shot={SHOTS.storyboard} />
            </div>
            <div className="features-split-text">
              <div className="features-tag">Storyboard</div>
              <h2 id="fantasy-storyboard-heading">Plot on cards linked to your cast</h2>
              <p>
                Lay out your plot on an open canvas of sticky notes and frames. Link each note
                to the character, place or event it turns on, so the plan and the world stay in
                step.
              </p>
            </div>
          </div>
        </section>

        <section className="features-split" aria-labelledby="fantasy-finish-heading">
          <div className="container features-split-inner">
            <div className="features-split-text">
              <div className="features-tag">Finish the book</div>
              <h2 id="fantasy-finish-heading">From draft to finished book</h2>
              <p>
                Chaptery's editor sets your text in your book's design as you write, and Print
                shows every page before it prints. Add Fantasy or Sci-fi scene divider
                ornaments, set your book's design, then print it, or export a print-ready PDF,
                EPUB or Word file.
              </p>
            </div>
            <div className="features-split-visual">
              <FantasyShot shot={SHOTS.print} />
            </div>
          </div>
        </section>

        <section className="features-split features-split--alt" aria-labelledby="fantasy-compare-heading">
          <div className="container fantasy-text-section">
            <h2 id="fantasy-compare-heading">How Chaptery compares</h2>
            <p>
              Fantasy authors often look at these tools too. Each does something well. Here's
              how Chaptery differs, from each tool's own website as of 1 November 2026.
            </p>
            <p>
              <strong>World Anvil</strong> is a worldbuilding platform with article templates,
              interactive maps, timelines and family trees, and its Master plan and above add
              Manuscripts, a novel editor linked to your world. Chaptery starts from the book: a
              desktop app for Mac and Windows whose editor sets your text in your book's design
              as you write.
            </p>
            <p>
              <strong>Campfire</strong> is a modular writing and worldbuilding app for the
              browser, Mac, Windows and phones: you choose the modules you want, from maps to
              languages, and linked projects handle a series. Chaptery has nothing to add on.
              One subscription gets you the Mac and Windows app, with an editor that sets your
              text in your book's design as you write, plus a World Builder with a relationship
              map and a Storyboard that you build on trychaptery.com and keep open beside your
              chapter.
            </p>
            <p>
              <strong>Scrivener</strong> is a long-running writing app sold as a one-time
              licence per platform, with templates you can shape into character or location
              sheets. Chaptery is a subscription instead, one that covers both Mac and Windows,
              with a World Builder for characters, locations and events, a relationship map,
              and series support built in so characters and places carry across books.
            </p>
            <p>
              <Link to="/compare">Compare Chaptery with Scrivener, Atticus and Vellum</Link>
            </p>
          </div>
        </section>

        <section className="features-split" aria-labelledby="fantasy-faq-heading">
          <div className="container fantasy-text-section fantasy-faq">
            <h2 id="fantasy-faq-heading">Questions</h2>
            <div className="fantasy-faq-item">
              <h3>Does Chaptery write the book for me?</h3>
              <p>
                No. The book is yours, every word of it. Chaptery keeps track of your world,
                your plot and your progress. Chaptery has no AI built in: an optional Assistant
                runs on your own AI key or a local model, acts only when you ask, and is
                designed to automate tasks in Chaptery.
              </p>
            </div>
            <div className="fantasy-faq-item">
              <h3>Does it run on Mac and Windows?</h3>
              <p>
                Yes. One subscription covers the Mac app (Apple Silicon and Intel) and the
                Windows app (Windows 10 and 11).
              </p>
            </div>
            <div className="fantasy-faq-item">
              <h3>Can I use it on my phone?</h3>
              <p>
                Not for writing: there's no phone app, and you write in the desktop app on
                your Mac or PC. Your Bookshelf, World Builder, Storyboard and Writing Dashboard
                also open in your phone's web browser, so you can check your notes away from
                your desk.
              </p>
            </div>
            <div className="fantasy-faq-item">
              <h3>What does it cost?</h3>
              <p>
                {planPrice('monthly', currency)} a month or {planPrice('annual', currency)} a
                year, for everything. Try it free for 30 days with one book, no credit card needed.
              </p>
            </div>
          </div>
        </section>

        <section className="features-cta">
          <div className="container">
            <h2>Try it with your own world</h2>
            <p>30 days free, every feature, one book. No credit card.</p>
            <div className="hero-actions">
              <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('fantasy-footer')}>
                Start Free Trial
              </Link>
              <Link to="/pricing" className="btn-secondary-large">View Pricing</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FantasyPage;
